import { useEffect, useState } from 'react';

/**
 * Custom hook to track the visibility of a DOM element by its ID.
 * Uses MutationObserver to automatically detect when the element appears or disappears.
 *
 * @param id - The ID of the element to watch for visibility
 * @returns boolean indicating whether the element is currently present in the DOM
 */
function useElementVisibilityById(id: string): boolean {
  const [isVisible, setIsVisible] = useState(() =>
    // Initial check for the element presence
    !!document.getElementById(id));

  useEffect(() => {
    const checkVisibility = () => {
      setIsVisible(!!document.getElementById(id));
    };

    // Run on mount in case it changes since initial render
    checkVisibility();

    // Create observer to watch for DOM changes
    const observer = new MutationObserver(checkVisibility);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [id]);

  return isVisible;
}

export default useElementVisibilityById;
